import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, useTheme, ButtonBase } from "@mui/material";
import * as React from "react";

import {
  TimeDisplayMode,
  useTimeDisplayMode,
} from "@/shared/components/visualization/WorkspaceCtx";
import { usePopupState } from "@/shared/hooks";
import { actions, useVizDispatch } from "@/shared/state/visualization";

import {
  ColumnFiltersAction,
  DeleteFilterAction,
  SetFilterAction,
} from "../columnFilters/actions";
import { ColumnDefinition } from "../columnFilters/columnDefinition";
import { ColumnFilterPopover } from "../columnFilters/ColumnFilterPopover";
import { ColumnFiltersState } from "../columnFilters/state";
import panelStyles from "../LogPanel.module.css";

import headerStyles from "./TableHeader.module.css";

function getTimeColumnHeader(mode: TimeDisplayMode): string {
  switch (mode) {
    case TimeDisplayMode.Elapsed:
    case TimeDisplayMode.Log:
      return "Time (s)";
    case TimeDisplayMode.Local:
    case TimeDisplayMode.UTC:
      return "Time";
  }
}

interface LogTableHeader {
  columnDefinitions: ColumnDefinition[];
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  canFilter: boolean;
  panelId: string;
}

export function TableHeader({
  columnDefinitions,
  panelId,
  canFilter,
  columnFilters,
  columnFiltersDispatch,
}: LogTableHeader) {
  const theme = useTheme();
  const [timeDisplayMode] = useTimeDisplayMode();

  return (
    <div
      className={panelStyles.rowHeader}
      style={
        {
          "--background-color": theme.palette.background.default,
          borderBottom: theme.border.thin,
          fontWeight: theme.typography.fontWeightMedium,
        } as React.CSSProperties
      }
    >
      <h6 className={headerStyles.header}>
        {getTimeColumnHeader(timeDisplayMode)}
      </h6>
      {columnDefinitions.map((column) => (
        <ColumnHeader
          panelId={panelId}
          column={column}
          canFilter={canFilter}
          key={column.messagePathId}
          columnFilters={columnFilters}
          columnFiltersDispatch={columnFiltersDispatch}
        />
      ))}
    </div>
  );
}

function ColumnHeader({
  panelId,
  column,
  canFilter,
  columnFilters,
  columnFiltersDispatch,
}: {
  panelId: string;
  column: ColumnDefinition;
  canFilter: boolean;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
}) {
  const dispatch = useVizDispatch();
  const popup = usePopupState();
  const onFilterChanged = (changedFilter: string) => {
    if (changedFilter === "") {
      columnFiltersDispatch(
        new DeleteFilterAction({ columnId: column.messagePathId }),
      );
    } else {
      columnFiltersDispatch(
        new SetFilterAction({
          columnId: column.messagePathId,
          filter: changedFilter,
        }),
      );
    }
  };
  return (
    <h6 key={column.messagePathId} className={headerStyles.header}>
      {canFilter ? (
        <ButtonBase
          className={headerStyles.headerButton}
          component="span"
          title="Search column"
          onClick={(event) => {
            if (!popup.isOpen) {
              popup.open({ anchorEl: event.currentTarget });
            } else {
              popup.close();
            }
          }}
          sx={{
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          {column.name}
          <IconButton
            disableRipple
            aria-label="search column"
            size="small"
            className={headerStyles.headerIconButton}
          >
            <SearchIcon fontSize="small" className={headerStyles.headerIcon} />
          </IconButton>
        </ButtonBase>
      ) : (
        <span className={headerStyles.headerText}>{column.name}</span>
      )}
      <ColumnFilterPopover
        popup={popup}
        filter={columnFilters.filters[column.messagePathId] ?? ""}
        changeFilter={onFilterChanged}
      />
      <IconButton
        aria-label="remove column"
        onClick={() => {
          dispatch(
            actions.removeMessagePathFromLogPanel(
              panelId,
              column.messagePathId,
            ),
          );
        }}
        size="small"
        className={headerStyles.headerIconButton}
        title="Remove column"
      >
        <CloseIcon fontSize="small" className={headerStyles.headerIcon} />
      </IconButton>
    </h6>
  );
}
